import { toast } from '@bemor/web-kit';
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@bemor/web-kit/ui';

import type { components as admin } from '@/api/generated/admin';

/** Why one item of a bulk action was refused: the single action's error with its HTTP status (`BulkError`). */
export type BulkError = admin['schemas']['BulkError'];

/** One result of a bulk call (`QueueItemBulkResult`, `ApprovalBulkResult`), in the request's order. */
export interface BulkResult {
    readonly id: string;
    readonly ok: boolean;
    readonly error?: BulkError;
}

/** "3 granted, 1 failed", "3 granted", "1 failed". */
export function bulkSummary(results: readonly BulkResult[], verb: string) {
    const done = results.filter((r) => r.ok).length;
    const failed = results.length - done;
    const parts = [];
    if (done > 0) parts.push(`${done} ${verb}`);
    if (failed > 0) parts.push(`${failed} failed`);
    return parts.join(', ') || 'Nothing to do';
}

/**
 * After a bulk call: toast the outcome and say which items failed. The failed ids stay selected (the person can
 * retry them or clear the selection) and the dialog lists each one with the reason the single action gave.
 */
export function reportBulk(
    results: readonly BulkResult[],
    verb: string,
    keepSelected: (ids: string[]) => void,
): BulkResult[] {
    const failed = results.filter((r) => !r.ok);
    const summary = bulkSummary(results, verb);
    if (failed.length === 0) toast.success(summary);
    else toast.error(summary);
    keepSelected(failed.map((r) => r.id));
    return failed;
}

/** The items a bulk action refused, each with its reason (`message`, from the single action's error). */
export function BulkFailures({
    failures,
    labelOf,
    onClose,
}: {
    failures: readonly BulkResult[];
    labelOf: (id: string) => string;
    onClose: () => void;
}) {
    return (
        <Dialog
            open={failures.length > 0}
            onOpenChange={(o) => (o ? null : onClose())}
        >
            <DialogContent data-testid="bulk-failures">
                <DialogHeader>
                    <DialogTitle>
                        {failures.length === 1
                            ? 'One item was not done'
                            : `${failures.length} items were not done`}
                    </DialogTitle>
                    <DialogDescription>
                        They are still selected. Each one went through its own
                        action and was refused for the reason shown.
                    </DialogDescription>
                </DialogHeader>
                <ul className="flex max-h-80 flex-col gap-2 overflow-y-auto text-sm">
                    {failures.map((f) => (
                        <li key={f.id} className="flex min-w-0 flex-col">
                            <span className="truncate font-medium">
                                {labelOf(f.id)}
                            </span>
                            <span className="text-fg-2">
                                {f.error?.message ?? 'It could not be done.'}
                            </span>
                        </li>
                    ))}
                </ul>
                <DialogFooter>
                    <Button onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
